import { Bell, Building2, Globe, Grid3x3, LogOut, Menu, Search, Sun, Moon, Check, LayoutDashboard, Users, Share2, ArrowDownToLine, ArrowUpFromLine, ShieldCheck, LineChart, Layers } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/Input";
import { useLogout, useMe } from "@/hooks/useAuth";
import { useAdminNotifications } from "@/hooks/useMisc";
import { useNotifications, useUnreadCount, useMarkRead } from "@/hooks/useGame";
import { useCurrentTenant } from "@/hooks/useTenant";
import { useTheme } from "@/hooks/useTheme";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const PRIORITY_DOT: Record<string, string> = {
  critical: "bg-danger", high: "bg-warning", normal: "bg-primary", low: "bg-fg-subtle",
};

function NotificationBell() {
  const { data } = useAdminNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const rows = data?.rows ?? [];
  const count = data?.totals.all ?? 0;

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
        className="relative grid h-9 w-9 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg"
      >
        <Bell className="h-[18px] w-[18px]" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 z-50 rounded-lg border border-border bg-bg-elevated shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-border/50 text-sm font-semibold flex items-center justify-between">
            Notifications <span className="text-[11px] text-fg-subtle font-normal">{count} pending</span>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-border/30">
            {rows.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-fg-subtle">You're all caught up 🎉</p>
            ) : (
              rows.slice(0, 15).map((n) => (
                <button
                  key={n.id}
                  onClick={() => { setOpen(false); nav(n.link); }}
                  className="w-full text-left px-3 py-2 hover:bg-bg/60 transition flex items-start gap-2"
                >
                  <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${PRIORITY_DOT[n.priority] ?? "bg-primary"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{n.title}</p>
                    <p className="text-[11px] text-fg-subtle truncate">{n.body}</p>
                  </div>
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => { setOpen(false); nav("/admin/notifications"); }}
            className="w-full px-3 py-2 text-center text-xs text-primary hover:bg-bg/60 border-t border-border/50"
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
}

/** Trader-facing notification bell (binary wins, deposits, tournaments). */
function UserNotificationBell() {
  const { data: rows } = useNotifications();
  const { data: unread } = useUnreadCount();
  const markRead = useMarkRead();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const count = unread?.count ?? 0;
  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  function toggle() { const next = !open; setOpen(next); if (next && count > 0) markRead.mutate(undefined); }
  return (
    <div className="relative" ref={ref}>
      <button aria-label="Notifications" onClick={toggle}
        className="relative grid h-9 w-9 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg">
        <Bell className="h-[18px] w-[18px]" />
        {count > 0 && <span className="absolute -top-1 -right-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">{count > 9 ? "9+" : count}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 z-50 rounded-lg border border-border bg-bg-elevated shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-border/50 text-sm font-semibold">Notifications</div>
          <div className="max-h-96 overflow-y-auto divide-y divide-border/30">
            {!rows?.length ? <p className="px-3 py-8 text-center text-xs text-fg-subtle">You're all caught up 🎉</p> :
              rows.slice(0, 20).map((n) => (
                <button key={n.id} onClick={() => { setOpen(false); if (n.link) nav(n.link); }}
                  className={cn("w-full text-left px-3 py-2 hover:bg-bg/60 transition flex items-start gap-2", !n.readAt && "bg-primary/5")}>
                  <span className={cn("mt-1 h-2 w-2 rounded-full shrink-0", n.readAt ? "bg-fg-subtle" : "bg-primary")} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{n.title}</p>
                    {n.body && <p className="text-[11px] text-fg-subtle truncate">{n.body}</p>}
                    <p className="text-[10px] text-fg-subtle mt-0.5">{new Date(n.createdAt).toLocaleString()}</p>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

const LANGS: { code: string; label: string; rtl?: boolean }[] = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية", rtl: true },
  { code: "ur", label: "اردو", rtl: true },
  { code: "hi", label: "हिन्दी" },
  { code: "es", label: "Español" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "zh", label: "中文" },
];

function applyLang(code: string) {
  const l = LANGS.find((x) => x.code === code) ?? LANGS[0];
  document.documentElement.lang = l.code;
  document.documentElement.dir = l.rtl ? "rtl" : "ltr";
}

function LanguageMenu() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("fx_lang") || "en");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { applyLang(lang); }, []); // eslint-disable-line
  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const pick = (code: string) => {
    localStorage.setItem("fx_lang", code); applyLang(code); setLang(code); setOpen(false);
    if (code !== lang) window.location.reload(); // re-render every translated string
  };
  const active = LANGS.find((l) => l.code === lang) ?? LANGS[0];
  return (
    <div className="relative" ref={ref}>
      <button aria-label="Language" title={`Language: ${active.label}`} onClick={() => setOpen((v) => !v)}
        className="grid h-9 w-9 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg">
        <Globe className="h-[18px] w-[18px]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 z-50 rounded-lg border border-border bg-bg-elevated shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-border/50 text-[11px] uppercase tracking-wider text-fg-subtle">Language</div>
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => pick(l.code)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-bg/60 transition flex items-center justify-between">
              <span>{l.label}{l.rtl && <span className="ml-1.5 text-[9px] uppercase text-fg-subtle">RTL</span>}</span>
              {lang === l.code && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const APPS: { label: string; to: string; Icon: typeof Users }[] = [
  { label: "Dashboard", to: "/admin/dashboard", Icon: LayoutDashboard },
  { label: "Clients", to: "/admin/manage-client/trader-admin", Icon: Users },
  { label: "Partners", to: "/admin/ib/admin-report", Icon: Share2 },
  { label: "Deposits", to: "/admin/finance/admin-deposit", Icon: ArrowDownToLine },
  { label: "Withdrawals", to: "/admin/finance/admin-withdraw", Icon: ArrowUpFromLine },
  { label: "KYC", to: "/admin/kyc", Icon: ShieldCheck },
  { label: "MT5", to: "/admin/mt5/connection", Icon: LineChart },
  { label: "Groups", to: "/admin/groups/ib", Icon: Layers },
];

function AppsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button aria-label="Apps" title="Quick access" onClick={() => setOpen((v) => !v)}
        className="grid h-9 w-9 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg">
        <Grid3x3 className="h-[18px] w-[18px]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 z-50 rounded-lg border border-border bg-bg-elevated shadow-xl p-2">
          <div className="px-1.5 py-1 text-[11px] uppercase tracking-wider text-fg-subtle">Quick access</div>
          <div className="grid grid-cols-3 gap-1">
            {APPS.map(({ label, to, Icon }) => (
              <button key={to} onClick={() => { setOpen(false); nav(to); }}
                className="flex flex-col items-center gap-1.5 rounded-lg p-2.5 hover:bg-bg/60 transition">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-[18px] w-[18px]" /></span>
                <span className="text-[11px] text-fg-muted text-center leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Breadcrumbs() {
  const { pathname } = useLocation();
  const segs = pathname.split("/").filter(Boolean);
  if (!segs.length) return null;
  return (
    <nav className="text-xs text-fg-subtle">
      <ol className="flex items-center gap-1.5">
        {segs.map((s, i) => (
          <li key={i} className="flex items-center gap-1.5">
            <span className={i === segs.length - 1 ? "text-fg-muted capitalize" : "capitalize"}>
              {s.replace(/-/g, " ")}
            </span>
            {i < segs.length - 1 && <span className="opacity-40">/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Topbar({ onMenuToggle }: { onMenuToggle?: () => void } = {}) {
  const { data: me } = useMe();
  const { data: tenant } = useCurrentTenant();
  const logout = useLogout();
  const nav = useNavigate();
  const { resolved, toggle } = useTheme();
  const isTrader = me?.role === "trader" || me?.role === "ib" || me?.role === "affiliate";

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-bg/70 px-4 sm:px-5 backdrop-blur">
      {onMenuToggle && (
        <button
          onClick={onMenuToggle}
          aria-label="Open menu"
          className="lg:hidden grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}
      <div className="flex flex-1 items-center gap-3 min-w-0">
        {tenant?.tenant && !tenant.tenant.isPlatform && (
          <div
            className="hidden sm:flex items-center gap-2 rounded-lg px-2.5 py-1.5 ring-1"
            style={{
              borderColor: tenant.tenant.primaryColor ?? undefined,
              backgroundColor: (tenant.tenant.primaryColor ?? "#7c3aed") + "1a",
            }}
            title={`Tenant slug: ${tenant.tenant.slug}`}
          >
            {tenant.tenant.logoUrl ? (
              <img
                src={tenant.tenant.logoUrl}
                alt=""
                className="h-6 w-auto max-w-[60px] object-contain"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
            ) : (
              <Building2 className="h-4 w-4" style={{ color: tenant.tenant.primaryColor ?? undefined }} />
            )}
            <div className="leading-tight">
              <p className="text-[11px] font-semibold" style={{ color: tenant.tenant.primaryColor ?? undefined }}>{tenant.tenant.name}</p>
              <p className="text-[9px] uppercase tracking-wider text-fg-subtle font-mono">{tenant.tenant.slug}</p>
            </div>
          </div>
        )}
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
          <Input className="h-9 pl-9 bg-bg-elevated/60 border-border/60" placeholder={t("Search anything…")} />
          <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-border/60 bg-bg-subtle px-1.5 py-0.5 text-[10px] text-fg-subtle">
            ⌘ K
          </kbd>
        </div>
      </div>

      <div className="hidden md:block flex-1">
        <Breadcrumbs />
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={toggle}
          aria-label={resolved === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          title={resolved === "dark" ? "Light mode" : "Dark mode"}
          className="grid h-9 w-9 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg"
        >
          {resolved === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>
        <LanguageMenu />
        {isTrader ? null : <AppsMenu />}
        {isTrader ? <UserNotificationBell /> : <NotificationBell />}
        <button
          onClick={async () => {
            await logout.mutateAsync();
            nav("/login");
          }}
          className="ml-1 flex items-center gap-2 rounded-lg pl-1.5 pr-3 py-1 hover:bg-bg-elevated/60 transition"
          title={me?.email}
        >
          <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-grad-primary text-white text-xs font-semibold">
            {(me?.fullName ?? "A").slice(0, 1).toUpperCase()}
            <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-success ring-2 ring-bg" />
          </span>
          <div className="hidden md:flex flex-col items-start leading-tight">
            <span className="text-xs font-medium text-fg">
              {me?.fullName ?? "Admin"}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
              {(({ trader: "Client", ib: "Partner" } as Record<string,string>)[me?.role ?? ""] ?? me?.role) ?? "—"}
            </span>
          </div>
          <LogOut className="h-4 w-4 text-fg-subtle" />
        </button>
      </div>
    </header>
  );
}

